/**
 * Sales Library — top-level phase.
 *
 * Browse the company-wide pool of reusable assets (decks, PDFs,
 * case studies, pricing, security, legal, battlecards, one-pagers).
 * Admins can upload new assets, edit metadata, push new versions,
 * and archive. Members can browse + attach assets to their own
 * Buyer Spaces (the attach happens inside the Decision Room
 * Documents tab, not here).
 *
 * Backend: /api/library — see backend/routes/library.js.
 */

import { useEffect, useState, useMemo } from 'react';
import DocShareModal from './DocShareModal';
import {
  internalShareUrl, resolveInternalShareToken, selfPreviewLabel,
} from '../utils/internalShare';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const ASSET_TYPES = [
  { value: 'case_study', label: 'Case study' },
  { value: 'deck',       label: 'Deck' },
  { value: 'pricing',    label: 'Pricing' },
  { value: 'security',   label: 'Security' },
  { value: 'legal',      label: 'Legal' },
  { value: 'battlecard', label: 'Battlecard' },
  { value: 'one_pager',  label: 'One-pager' },
  { value: 'other',      label: 'Other' },
];
const TYPE_LABEL = Object.fromEntries(ASSET_TYPES.map((t) => [t.value, t.label]));

export default function SalesLibraryPhase({ authToken, isCompanyAdmin, currentUserName }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (searchQ.trim()) params.set('q', searchQ.trim());
      if (includeArchived) params.set('includeArchived', '1');
      const r = await fetch(`${API_BASE}/api/library/assets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const body = await r.json();
      setAssets(body.assets || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  // Reload on filter/search change. Debounce search.
  useEffect(() => {
    const t = setTimeout(load, searchQ ? 250 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filterType, searchQ, includeArchived]);

  const visibleAssets = useMemo(() => assets, [assets]);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)' }}>Library</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>
            Company-wide pool of decks, PDFs, case studies, pricing, and other sales assets. Reps pull assets into Decision Rooms with one click.
          </p>
        </div>
        {isCompanyAdmin && (
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            style={primaryBtn(false)}
          >
            + Upload asset
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setFilterType(null)}
          style={chipStyle(filterType === null)}
        >
          All
        </button>
        {ASSET_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setFilterType(t.value)}
            style={chipStyle(filterType === t.value)}
          >
            {t.label}
          </button>
        ))}
        <input
          type="text"
          placeholder="Search title, description, tags…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          style={{ ...inputStyle, width: 280, marginLeft: 'auto' }}
        />
        {isCompanyAdmin && (
          <label style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
        )}
      </div>

      {error && (
        <div style={errorStyle}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Loading library…</div>
      ) : visibleAssets.length === 0 ? (
        <div style={{ background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--room-text-muted, #64748b)' }}>
          {searchQ || filterType ? 'No assets match these filters.' : 'No assets yet. ' + (isCompanyAdmin ? 'Click "Upload asset" to add the first one.' : 'Ask an admin to upload some.')}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 14,
        }}>
          {visibleAssets.map((a) => (
            <AssetTile
              key={a.id}
              asset={a}
              onClick={() => setSelectedAssetId(a.id)}
            />
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          authToken={authToken}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); load(); }}
        />
      )}

      {selectedAssetId && (
        <AssetDetailDrawer
          assetId={selectedAssetId}
          authToken={authToken}
          isAdmin={isCompanyAdmin}
          currentUserName={currentUserName}
          onClose={() => setSelectedAssetId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ─── Tile ──────────────────────────────────────────────────────────

// Deterministic accent per asset type for the no-thumbnail placeholder,
// so the grid stays visually scannable even before thumbnails render.
const TYPE_ACCENT = {
  battlecard: '#8b5cf6', one_pager: '#6366f1', deck: '#0891b2', case_study: '#0d9488',
  pricing: '#b45309', security: '#475569', legal: '#64748b', other: '#94a3b8',
};

function AssetTile({ asset, onClick }) {
  const isArchived = !!asset.archivedAt;
  const isAdminOnly = asset.visibility === 'admin_only';
  const accent = TYPE_ACCENT[asset.assetType] || '#94a3b8';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: 'var(--room-card, #fff)',
        border: '1px solid var(--room-border, #e2e8f0)',
        borderRadius: 12,
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        opacity: isArchived ? 0.55 : 1,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Visual header (2026-08-23): page-1 thumbnail when the backend
          has rendered one; typed gradient placeholder otherwise. */}
      <div style={{
        height: 140, flexShrink: 0, position: 'relative',
        background: asset.thumbnailUrl
          ? 'var(--room-border-soft, #f1f5f9)'
          : `linear-gradient(135deg, ${accent}22 0%, ${accent}44 100%)`,
        borderBottom: '1px solid var(--room-border-soft, #f1f5f9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {asset.thumbnailUrl ? (
          <img
            src={asset.thumbnailUrl}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span style={{ fontSize: 34, fontWeight: 800, color: accent, opacity: 0.55, letterSpacing: '0.02em' }}>
            {(TYPE_LABEL[asset.assetType] || asset.assetType || '?').slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={typeBadgeStyle(asset.assetType)}>
          {TYPE_LABEL[asset.assetType] || asset.assetType}
        </span>
        {isAdminOnly && (
          <span style={{ fontSize: 10, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
            Admin only
          </span>
        )}
        {isArchived && (
          <span style={{ fontSize: 10, color: 'var(--room-text-muted, #64748b)', background: 'var(--room-border-soft, #f1f5f9)', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
            Archived
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--room-text-muted, #64748b)' }}>
          v{asset.currentVersion}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong, #0f172a)', lineHeight: 1.3, marginBottom: 4 }}>
        {asset.title}
      </div>
      {asset.description && (
        <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {asset.description}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', display: 'flex', gap: 10, alignItems: 'center' }}>
        <span>{asset.currentFilename}</span>
        {asset.attachCount != null && asset.attachCount > 0 && (
          <span style={{ marginLeft: 'auto' }}>Used in {asset.attachCount} Space{asset.attachCount === 1 ? '' : 's'}</span>
        )}
      </div>
      {Array.isArray(asset.tags) && asset.tags.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {asset.tags.slice(0, 4).map((t) => (
            <span key={t} style={tagPillStyle}>{t}</span>
          ))}
        </div>
      )}
      </div>
    </button>
  );
}

// ─── Upload modal ──────────────────────────────────────────────────

function UploadModal({ authToken, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assetType, setAssetType] = useState('deck');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [industries, setIndustries] = useState('');
  const [useCases, setUseCases] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!file) { setError('Pick a file first.'); return; }
    if (!title.trim()) { setError('Title is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      if (description.trim()) fd.append('description', description.trim());
      fd.append('assetType', assetType);
      fd.append('tags', tags);
      fd.append('visibility', visibility);
      fd.append('relevantIndustries', industries);
      fd.append('relevantUseCases', useCases);
      const r = await fetch(`${API_BASE}/api/library/assets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Upload failed (${r.status})`);
      onUploaded();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Upload library asset" onClose={onClose} disabled={busy}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="File">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={busy}
            style={{ fontFamily: 'inherit', fontSize: 13 }}
          />
          <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', marginTop: 4 }}>
            PDF · DOCX · XLSX · PPTX · PNG · JPG · WebP · GIF · TXT · CSV · ZIP, up to 25 MB
          </div>
        </Field>
        <Field label="Title" required>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} style={inputStyle} />
        </Field>
        <Field label="Description (optional)">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy} rows={3} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
        </Field>
        <Field label="Type">
          <select value={assetType} onChange={(e) => setAssetType(e.target.value)} disabled={busy} style={inputStyle}>
            {ASSET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Tags (comma-separated)">
          <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} disabled={busy} placeholder="e.g. enterprise, q3-2026, late-stage" style={inputStyle} />
        </Field>
        <Field label="Visibility">
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} disabled={busy} style={inputStyle}>
            <option value="all">Everyone in your company</option>
            <option value="admin_only">Admins only (hidden from members)</option>
          </select>
        </Field>
        <Field label="Relevant industries (optional, for suggestions)">
          <input type="text" value={industries} onChange={(e) => setIndustries(e.target.value)} disabled={busy} placeholder="e.g. saas, healthcare, fintech" style={inputStyle} />
        </Field>
        <Field label="Relevant use cases (optional, for suggestions)">
          <input type="text" value={useCases} onChange={(e) => setUseCases(e.target.value)} disabled={busy} placeholder="e.g. enterprise-deal, security-review" style={inputStyle} />
        </Field>
        {error && <div style={errorStyle}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button type="button" onClick={submit} disabled={busy || !file || !title.trim()} style={primaryBtn(busy || !file || !title.trim())}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
          <button type="button" onClick={onClose} disabled={busy} style={secondaryBtn(busy)}>Cancel</button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── Detail drawer ────────────────────────────────────────────────

function AssetDetailDrawer({ assetId, authToken, isAdmin, currentUserName, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showVersionUpload, setShowVersionUpload] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // Edit form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assetType, setAssetType] = useState('deck');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [industries, setIndustries] = useState('');
  const [useCases, setUseCases] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/library/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const body = await r.json();
      setDetail(body);
      setTitle(body.asset.title || '');
      setDescription(body.asset.description || '');
      setAssetType(body.asset.assetType || 'deck');
      setTags((body.asset.tags || []).join(', '));
      setVisibility(body.asset.visibility || 'all');
      setIndustries((body.asset.relevantIndustries || []).join(', '));
      setUseCases((body.asset.relevantUseCases || []).join(', '));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [assetId]);

  const saveEdits = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/library/assets/${assetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || null,
          assetType,
          tags,
          visibility,
          relevantIndustries: industries,
          relevantUseCases: useCases,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Save failed (${r.status})`);
      setEditing(false);
      load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!window.confirm('Archive this asset? It stays usable in existing Spaces but won\'t appear in the library browse view.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/library/assets/${assetId}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Archive failed (${r.status})`);
      onChanged?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const restore = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/library/assets/${assetId}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Restore failed (${r.status})`);
      load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadVersion = async (version) => {
    try {
      const r = await fetch(`${API_BASE}/api/library/assets/${assetId}/download${version ? `?version=${version}` : ''}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `asset-v${version || ''}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Standalone view (2026-08-23). Replaces the blob-into-an-iframe
  // modal this used to be. That modal downloaded the WHOLE file before
  // showing anything (an auth header can't ride an <iframe src>, so the
  // bytes had to come through fetch) and then handed a PDF to whatever
  // viewer the browser felt like using.
  //
  // Now: mint (or reuse) an INTERNAL share link for the asset and open
  // /i/<token> in a new tab. Same fast page-image renderer the buyer
  // gets, no whole-file download, and the rep sees a real page instead
  // of a preview of a preview. Internal links are the right vehicle —
  // they record to internal_share_views, never to buyer engagement.
  const previewableMime = (mime) => !!mime && (
    mime === 'application/pdf'
    || mime.startsWith('image/')
    || mime.startsWith('video/')
    || mime.startsWith('audio/')
  );

  // Reuse is matched on the EXACT label we would mint, which scopes it
  // to this user by construction: a colleague-facing share never gets
  // absorbed into a rep's own re-reads, and two reps previewing the
  // same asset each keep their own link (and their own view trail)
  // instead of quietly sharing one. The rule now lives in
  // utils/internalShare so the Decision Room's document cards, which
  // do the same thing for `buyer_space_doc`, share one copy of it.
  const previewLabel = selfPreviewLabel(currentUserName);

  const openStandalone = async () => {
    // The tab has to be opened synchronously off the click or popup
    // blockers eat it — the token arrives a network round-trip later
    // and gets navigated into the already-open tab.
    let tab = null;
    try { tab = window.open('', '_blank'); } catch (_) { tab = null; }
    setBusy(true);
    setError(null);
    try {
      const token = await resolveInternalShareToken({
        apiBase: API_BASE,
        authToken,
        artifactType: 'document',
        artifactId: assetId,
        recipientLabel: previewLabel,
      });

      const url = internalShareUrl(token);
      if (tab && !tab.closed) {
        try { tab.opener = null; } catch (_) { /* same-origin anyway */ }
        tab.location.replace(url);
      } else {
        const opened = window.open(url, '_blank', 'noopener');
        if (!opened) setError('Your browser blocked the preview tab. Allow pop-ups for this site and try again.');
      }
    } catch (err) {
      if (tab && !tab.closed) { try { tab.close(); } catch (_) { /* noop */ } }
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={detail?.asset?.title || 'Library asset'} onClose={onClose} disabled={busy} wide>
      {loading ? (
        <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>
      ) : !detail ? (
        <div style={errorStyle}>{error || 'Asset not found.'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div style={errorStyle}>{error}</div>}

          {!editing ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={typeBadgeStyle(detail.asset.assetType)}>{TYPE_LABEL[detail.asset.assetType]}</span>
                <span style={{ fontSize: 11, color: 'var(--room-text-muted, #64748b)' }}>v{detail.asset.currentVersion}</span>
                {detail.asset.visibility === 'admin_only' && (
                  <span style={{ fontSize: 10, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Admin only</span>
                )}
                {detail.asset.archivedAt && (
                  <span style={{ fontSize: 10, color: 'var(--room-text-muted, #64748b)', background: 'var(--room-border-soft, #f1f5f9)', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Archived</span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setShowShare(true)} disabled={busy} style={secondaryBtn(busy)}>
                    Share
                  </button>
                  {previewableMime(detail.asset.currentMimeType) && (
                    <button type="button" onClick={openStandalone} disabled={busy} style={secondaryBtn(busy)}>
                      View
                    </button>
                  )}
                  <button type="button" onClick={() => downloadVersion(null)} style={secondaryBtn(busy)}>
                    Download current
                  </button>
                  {isAdmin && !detail.asset.archivedAt && (
                    <>
                      <button type="button" onClick={() => setEditing(true)} disabled={busy} style={secondaryBtn(busy)}>Edit</button>
                      <button type="button" onClick={() => setShowVersionUpload(true)} disabled={busy} style={secondaryBtn(busy)}>Upload new version</button>
                      <button type="button" onClick={archive} disabled={busy} style={dangerBtn(busy)}>Archive</button>
                    </>
                  )}
                  {isAdmin && detail.asset.archivedAt && (
                    <button type="button" onClick={restore} disabled={busy} style={secondaryBtn(busy)}>Restore</button>
                  )}
                </div>
              </div>

              {detail.asset.description && (
                <p style={{ margin: 0, color: 'var(--room-text-body, #334155)', lineHeight: 1.5 }}>{detail.asset.description}</p>
              )}

              {Array.isArray(detail.asset.tags) && detail.asset.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {detail.asset.tags.map((t) => <span key={t} style={tagPillStyle}>{t}</span>)}
                </div>
              )}

              {/* Version history */}
              <Section title="Version history">
                {detail.versions.map((v) => (
                  <div key={v.version} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--room-border-soft, #f1f5f9)', fontSize: 13 }}>
                    <span style={{ fontWeight: 600, minWidth: 30 }}>v{v.version}</span>
                    <span style={{ color: 'var(--room-text-body, #475569)' }}>{v.filename}</span>
                    {v.change_note && <span style={{ color: 'var(--room-text-muted, #64748b)', fontStyle: 'italic' }}>— {v.change_note}</span>}
                    <span style={{ marginLeft: 'auto', color: 'var(--room-text-subtle, #94a3b8)', fontSize: 11 }}>
                      {new Date(v.uploaded_at).toLocaleDateString()} by {[v.uploader_first, v.uploader_last].filter(Boolean).join(' ') || v.uploader_username || 'unknown'}
                    </span>
                    {/* Standalone view always serves the asset's CURRENT
                        version — internal links resolve to it by design.
                        Older rows say so rather than quietly opening the
                        wrong file; Download is still per-version. */}
                    {previewableMime(v.mime_type || detail.asset.currentMimeType) && (
                      Number(v.version) === Number(detail.asset.currentVersion) ? (
                        <button type="button" onClick={openStandalone} disabled={busy} style={smallBtn}>View</button>
                      ) : (
                        <span style={{ fontSize: 10, color: 'var(--room-text-subtle, #94a3b8)' }} title="Standalone view always opens the current version">
                          download to view
                        </span>
                      )
                    )}
                    <button type="button" onClick={() => downloadVersion(v.version)} style={smallBtn}>Download</button>
                  </div>
                ))}
              </Section>

              {/* Spaces using */}
              {Array.isArray(detail.spaces) && detail.spaces.length > 0 && (
                <Section title={`Used in ${detail.spaces.length} Decision Room${detail.spaces.length === 1 ? '' : 's'}`}>
                  {detail.spaces.map((s) => (
                    <div key={s.id} style={{ padding: '6px 0', fontSize: 13, color: 'var(--room-text-body, #334155)', borderBottom: '1px solid var(--room-border-soft, #f1f5f9)' }}>
                      <strong>{s.prospect_company_name}</strong>
                      <span style={{ color: 'var(--room-text-subtle, #94a3b8)', marginLeft: 8 }}>· {s.status}</span>
                      {s.library_asset_version && (
                        <span style={{ color: '#a16207', marginLeft: 8, fontSize: 11 }}>pinned to v{s.library_asset_version}</span>
                      )}
                    </div>
                  ))}
                </Section>
              )}
            </>
          ) : (
            <>
              <Field label="Title"><input type="text" value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} style={inputStyle} /></Field>
              <Field label="Description"><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} /></Field>
              <Field label="Type">
                <select value={assetType} onChange={(e) => setAssetType(e.target.value)} disabled={busy} style={inputStyle}>
                  {ASSET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Tags"><input type="text" value={tags} onChange={(e) => setTags(e.target.value)} disabled={busy} style={inputStyle} /></Field>
              <Field label="Visibility">
                <select value={visibility} onChange={(e) => setVisibility(e.target.value)} disabled={busy} style={inputStyle}>
                  <option value="all">Everyone</option>
                  <option value="admin_only">Admins only</option>
                </select>
              </Field>
              <Field label="Relevant industries"><input type="text" value={industries} onChange={(e) => setIndustries(e.target.value)} disabled={busy} style={inputStyle} /></Field>
              <Field label="Relevant use cases"><input type="text" value={useCases} onChange={(e) => setUseCases(e.target.value)} disabled={busy} style={inputStyle} /></Field>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={saveEdits} disabled={busy || !title.trim()} style={primaryBtn(busy || !title.trim())}>{busy ? 'Saving…' : 'Save'}</button>
                <button type="button" onClick={() => { setEditing(false); load(); }} disabled={busy} style={secondaryBtn(busy)}>Cancel</button>
              </div>
            </>
          )}

          {showVersionUpload && (
            <VersionUploadInline
              assetId={assetId}
              authToken={authToken}
              onClose={() => setShowVersionUpload(false)}
              onUploaded={() => { setShowVersionUpload(false); load(); onChanged?.(); }}
            />
          )}

          {/* Tracked doc share links (doc-share-viewer). Sits above the
              asset modal at zIndex 1100 — see DocShareModal.jsx. */}
          {showShare && (
            <DocShareModal
              asset={{ id: assetId, ...detail.asset }}
              authToken={authToken}
              onClose={() => setShowShare(false)}
            />
          )}
        </div>
      )}
    </ModalShell>
  );
}

function VersionUploadInline({ assetId, authToken, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [changeNote, setChangeNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!file) { setError('Pick a file first.'); return; }
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (changeNote.trim()) fd.append('changeNote', changeNote.trim());
      const r = await fetch(`${API_BASE}/api/library/assets/${assetId}/versions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Upload failed (${r.status})`);
      onUploaded();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ background: 'var(--room-surface, #f8fafc)', border: '1px dashed var(--room-border, #cbd5e1)', borderRadius: 10, padding: 14, marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Upload new version</div>
      <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy} style={{ fontFamily: 'inherit', fontSize: 13, marginBottom: 8 }} />
      <input type="text" placeholder="Change note (e.g. Updated Q3 pricing)" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} disabled={busy} style={{ ...inputStyle, width: '100%', marginBottom: 8 }} />
      {error && <div style={errorStyle}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={submit} disabled={busy || !file} style={primaryBtn(busy || !file)}>{busy ? 'Uploading…' : 'Upload version'}</button>
        <button type="button" onClick={onClose} disabled={busy} style={secondaryBtn(busy)}>Cancel</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', marginTop: 6 }}>
        Spaces with always-latest pinning will see the new version on next download. Spaces pinned to v1 stay on v1.
      </div>
    </div>
  );
}

// ─── Shared shell + helpers ────────────────────────────────────────

function ModalShell({ title, onClose, disabled, wide, children }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !disabled) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: 'var(--room-card, #fff)', borderRadius: 14,
        width: wide ? 'min(720px, 100%)' : 'min(520px, 100%)',
        maxHeight: '92vh', overflow: 'auto',
        padding: 22, boxShadow: '0 20px 60px rgba(15,23,42,0.3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--room-text-strong, #0f172a)' }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--room-text-muted, #64748b)', padding: 4 }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--room-text-body, #475569)', marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </div>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────

function chipStyle(active) {
  return {
    padding: '5px 12px', borderRadius: 999,
    border: `1px solid ${active ? '#7c3aed' : 'var(--room-border, #cbd5e1)'}`,
    background: active ? '#7c3aed' : 'var(--room-card, #fff)',
    color: active ? '#fff' : 'var(--room-text-body, #334155)',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  };
}
function typeBadgeStyle(_type) {
  return {
    fontSize: 10, fontWeight: 700, color: '#5b21b6', background: '#ede9fe',
    padding: '2px 8px', borderRadius: 999,
    textTransform: 'uppercase', letterSpacing: 0.4,
  };
}
const tagPillStyle = {
  fontSize: 10, color: 'var(--room-text-body, #475569)', background: 'var(--room-border-soft, #f1f5f9)',
  padding: '2px 8px', borderRadius: 999,
};
const inputStyle = {
  padding: '8px 12px', borderRadius: 8,
  border: '1px solid #cbd5e1', fontSize: 13, fontFamily: 'inherit',
  width: '100%', boxSizing: 'border-box',
};
const errorStyle = {
  padding: '8px 12px', borderRadius: 8,
  background: '#fee2e2', border: '1px solid #fecaca',
  color: '#991b1b', fontSize: 13,
};
function primaryBtn(disabled) {
  return {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600,
    fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
  };
}
function secondaryBtn(disabled) {
  return {
    padding: '8px 14px', borderRadius: 8,
    border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
  };
}
function dangerBtn(disabled) {
  return {
    padding: '8px 14px', borderRadius: 8,
    border: '1px solid #fecaca', background: 'var(--room-card, #fff)', color: '#b91c1c',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
  };
}
const smallBtn = {
  padding: '4px 10px', borderRadius: 6,
  border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
  fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
};
